import _ from 'lodash';
import moment from 'moment';
import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import ContractOwnershipList from 'src/models/ContractOwnershipList';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';

export interface IManageLeaseholdDialog2Props {
    navigate: (path: string) => void;
    onCloseDialog?: () => void;
}

export type IManageLeaseholdDialog2StateProps = {
    ownershipId: number;
    horizonNumber?: number;
};

type IManageLeaseholdDialog2PageProps = IManageLeaseholdDialog2Props & IManageLeaseholdDialog2StateProps;

const ManageLeaseholdDialog2 = (props: IManageLeaseholdDialog2PageProps) => {
    // tslint:disable: max-line-length

    const appContext = useContext<IAppContextState>(AppContext);
    const { get, response } = useFetch();
    const { getValues, register } = useForm();
    const [isLeaseholdSearchLoading, setIsLeaseholdSearchLoading] = useState(false);
    const [contracts, setContracts] = useState<Array<ContractOwnershipList>>(null);
    const [searchResults, setSearchResults] = useState<ContractOwnershipList>(undefined);
    const [isContractFound, setIsContractFound] = useState(undefined);
    const [typeSelected, setTypeSelected] = useState(false);

    const onNavigate = (contractId: number, contractholdId: number, contractType: string) => {
        let navPath = '';
        switch (contractType) {
            case 'Lease':
                navPath = `/ownership/${props.ownershipId}/leases/${contractId}/leaseholds/${contractholdId}`;
                break;
            case 'Surface Waiver':
                navPath = `/ownership/${props.ownershipId}/surface-waivers/${contractId}/surface-waiver-holds/${contractholdId}`;
                break;
            case 'Solar Lease':
                navPath = `/ownership/${props.ownershipId}/solar-leases/${contractId}/solar-lease-holds/${contractholdId}`;
                break;
            case 'Battery Storage':
                navPath = `/ownership/${props.ownershipId}/battery-storage/${contractId}/battery-storage-holds/${contractholdId}`;
                break;
            case 'Brine Lease':
                navPath = `/ownership/${props.ownershipId}/brine-leases/${contractId}/brine-lease-holds/${contractholdId}`;
                break;    
            default:
                break;
        }
        props.navigate(navPath);
        props.onCloseDialog();
    };

    useEffect(() => {
        if (props.ownershipId > 0) {
            setSearchResults(undefined);
            setIsContractFound(undefined);
            getContracts();
        }
    }, [props.ownershipId]);

    const getContracts = async () => {
        const result: Array<ContractOwnershipList> = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/ownershipId/${props.ownershipId}/contracts/true`
        );

        if (response.ok) {
            setContracts(result);
        }
    };

    const onSearchLeases = useCallback(
        _.debounce(() => {
            let rep = getValues();
            if (rep.searchType?.length > 0 && rep.searchValue?.length > 0) {
                searchContracts(rep.searchType, rep.searchValue);
            }
        }, 800),
        []
    );

    const checkType = () => {
        let rep = getValues();
        setTypeSelected(rep.searchType?.length > 0);
        onSearchLeases();
    };

    const searchContracts = async (searchType: string, searchValue: string) => {
        setIsLeaseholdSearchLoading(true);
        let contractPath: string;
        if (searchType === 'Lease') {
            contractPath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/lease-number/${searchValue}`;
        } else {
            contractPath = `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/${searchType}/contract-number/${searchValue}`;
        }

        const results: ContractOwnershipList = await get(contractPath);

        if (response.ok && results.agreementId !== undefined && (results.isInHand || !results.isGeneratedAgreement)) {
            setSearchResults(results);
            setIsContractFound(true);
        } else {
            setSearchResults(undefined);
            setIsContractFound(false);
        }
        setIsLeaseholdSearchLoading(false);
    };

    const contractCard = (contract: ContractOwnershipList, index: number, nav: boolean, newContract: boolean) => {
        return (
            <div
                style={{ backgroundColor: contract.isActiveLease ? '#ffff7d' : 'white', borderBottom: '1px solid grey', padding: '.5rem', display: 'flex' }}
                onClick={
                    !newContract
                        ? () => onNavigate(contract.agreementId, contract.agreementholdId, contract.agreementType)
                        : () => onNavigate(contract.agreementId, 0, contract.agreementType)
                }
                className={nav ? 'clickable' : 'undefined'}
                key={index}
            >
                <div style={{ width: '12%' }}>{contract.agreementType || 'N/A'}</div>

                <div style={{ width: '10%' }}>{contract.agreementNumber ? contract.agreementNumber : contract.leaseNumber || 'N/A'}</div>

                <div style={{ width: '10%' }}>{`${contract.book} / ${contract.page}`}</div>

                <div style={{ width: '10%' }}>{contract.agreementDate ? moment(contract.agreementDate)?.format('MM/DD/YYYY') : 'N/A'}</div>

                <div style={{ width: '21%' }}>{contract.originalLessor || 'N/A'}</div>

                <div style={{ width: '21%' }}>{contract.originalLessee || 'N/A'}</div>

                <div style={{ width: '14%' }}>{contract.isTopLease ? 'Top Lease' : contract.isActiveLease ? 'Active' : 'Historical'}</div>
            </div>
        );
    };

    const contractCardContainer = () => {
        if (contracts?.length > 0) {
            return contracts
                .filter((l) => l.isInHand === true || !l.isGeneratedAgreement)
                .sort((a, b) => { // sort by Agreement / Ownership Type first
                    if (a.agreementType > b.agreementType) {
                        return -1;
                    }
                    if (a.agreementType < b.agreementType) {
                        return 1;
                    }
                    return 0;
                }) // send Active Leases to the top by sorting it second
                .sort((a, b) => {
                    if (a.isActiveLease > b.isActiveLease) {
                        return -1;
                    }
                    if (a.isActiveLease < b.isActiveLease) {
                        return 1;
                    }
                    return 0;
                })
                .map((contract: ContractOwnershipList, index) => {
                    return contractCard(contract, index, true, false);
                });
        } else {
            return <div style={{ textAlign: 'center' }}>--No InHanded Contract Ownerships have been attached to this Ownership--</div>;
        }
    };

    const contractList = () => {
        return (
            <div style={{ marginBottom: '1rem' }}>
                <div>
                    <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                        <legend className="legend">Attached Contract Ownerships</legend>
                    </fieldset>
                </div>

                <div style={{ display: 'flex', padding: '.5rem' }}>
                    <div style={{ width: '12%' }}>Ownership Type</div>
                    <div style={{ width: '10%' }}>Ownership Number</div>
                    <div style={{ width: '10%' }}>Vol/Page | Inst#</div>
                    <div style={{ width: '10%' }}>Ownership Date</div>
                    <div style={{ width: '21%' }}>Lessor</div>
                    <div style={{ width: '21%' }}>Lessee</div>
                    <div style={{ width: '14%' }}>Status</div>
                </div>

                <div>{contractCardContainer()}</div>
            </div>
        );
    };

    const leaseholdSearchResults = () => {
        const contractIdSearchValue = (document.getElementById('searchValue') as HTMLInputElement)?.value;
        const contractTypeSearchValue = (document.getElementById('searchType') as HTMLInputElement)?.value;

        if (isLeaseholdSearchLoading) {
            return <div style={{ textAlign: 'center' }}>---Loading---</div>;
        } else if (searchResults) {
            return (
                <div>
                    <div style={{ display: 'flex', padding: '.5rem' }}>
                        <div style={{ width: '12%' }}>Ownership Type</div>
                        <div style={{ width: '10%' }}>Ownership Number</div>
                        <div style={{ width: '10%' }}>Vol/Page | Inst#</div>
                        <div style={{ width: '10%' }}>Ownership Date</div>
                        <div style={{ width: '21%' }}>Lessor</div>
                        <div style={{ width: '21%' }}>Lessee</div>
                        <div style={{ width: '14%' }}>Status</div>
                    </div>

                    <div>{contractCard(searchResults, 0, true, true)}</div>
                </div>
            );
        } else if (contractIdSearchValue && contractTypeSearchValue) {
            return <div style={{ textAlign: 'center' }}>---No Contract Ownership Found---</div>;
        } else {
            return <div style={{ textAlign: 'center' }}>---Enter Contract Ownership Number And Type To Search For Existing Contracts---</div>;
        }
    };

    const attachContract = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        e.preventDefault();
        let rep = getValues();

        if (searchResults) {
            onNavigate(searchResults.agreementId, 0, rep.searchType);
            props.onCloseDialog();
        }
    };

    const determineLeaseholdValidity = () => {
        if (isContractFound === true) {
            return (
                <span key="lh1">
                    <span>
                        <i id="LeaseholdFound" style={{ color: 'green', fontSize: '2rem' }} className="far fa-check-circle" />
                    </span>
                    <span style={{ color: 'green', marginLeft: '.5rem' }}>Valid Contract Ownership Number</span>
                    <input
                        className="clickable"
                        style={{ height: '2rem', border: '0', backgroundColor: '#6393C1', color: 'white', marginLeft: '1rem' }}
                        type="button"
                        onClick={(e) => attachContract(e)}
                        value="Attach Contract"
                    />
                </span>
            );
        } else if (isContractFound === false) {
            return (
                <span key="lh2">
                    <i id="NoLeaseholdFound" style={{ color: 'red', fontSize: '2rem' }} className="fas fa-exclamation-triangle" />
                    <span style={{ color: 'red', marginLeft: '.5rem' }}>Invalid Contract Ownership Number</span>
                </span>
            );
        }
        return <span />;
    };

    const leaseSearch = () => {
        return (
            <div style={{ marginBottom: '1rem' }}>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">Search Existing Contract Ownerships</legend>
                </fieldset>

                <div>
                    <label style={{ marginRight: '1rem' }}>Contract Ownership Type:</label>

                    <select
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem' }}
                        name="searchType"
                        id="searchType"
                        ref={register}
                        onChange={() => checkType()}
                    >
                        <option key={0} disabled={true} selected={true} hidden={true} value={''}>
                            -- Select --
                        </option>
                        <option key={1} value={'Lease'}>
                            Oil and Gas Lease
                        </option>
                        <option key={3} value={'Surface Waiver'}>
                            Surface Waiver
                        </option>
                        <option key={4} value={'Solar Lease'}>
                            Solar Lease
                        </option>
                        <option key={5} value={'Battery Storage'}>
                            Battery Storage
                        </option>
                        <option key={6} value={'Brine Lease'}>
                            Brine Lease
                        </option>
                    </select>

                    <label style={{ marginRight: '1rem' }}>Contract Ownership Number:</label>

                    <input
                        style={{ height: '3rem', border: '0', borderBottom: '1px solid grey', marginRight: '1rem' }}
                        type="text"
                        name="searchValue"
                        id="searchValue"
                        ref={register}
                        onChange={() => onSearchLeases()}
                    />
                    {determineLeaseholdValidity()}
                </div>

                <div>
                    <div>{leaseholdSearchResults()}</div>
                </div>
            </div>
        );
    };

    const createNewLease = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        let rep = getValues();

        onNavigate(0, 0, rep.searchType);
    };

    return (
        <Dialog title="Manage Contract Ownerships">
            <div style={{ width: '60vw' }}>
                <div>{contractList()}</div>

                <div>{leaseSearch()}</div>

                <div>
                    <Button text="New Contract" intent="primary" onClick={(e) => createNewLease(e)} disabled={!typeSelected} />
                    {!typeSelected && <span> Select Contract Ownership Type to continue.</span>}
                </div>
            </div>
        </Dialog>
    );
};

export default ManageLeaseholdDialog2;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ManageLeaseholdDialog.redesign.tsx